import { useState, useEffect, useCallback, useRef } from "react";
import axios, { isAxiosError } from "axios";
import { SafeAvailabilityRule } from "../types";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { getMinBookableDate } from "../utils/bookingLeadTime";

function isRateLimited(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 429;
}

function isTransientAvailabilityError(error: unknown): boolean {
  if (!isAxiosError(error)) return true;
  const status = error.response?.status;
  return !status || status === 429 || status >= 500;
}

/**
 * Spots for a date straight from the availability rules. Also the fallback
 * when the availability API is rate-limited or unreachable. Exported for
 * validations that must not depend on the month-capped availableDates list
 * (which only covers the currently viewed month).
 */
export function getClientSpotsForDate(
  date: Date,
  rules: SafeAvailabilityRule[]
): {
  participants: number;
  extraDivers: number;
  extraSnorkelers: number;
} | null {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  const dayOfWeek = normalized.getDay() === 0 ? 7 : normalized.getDay();

  let bestRule: SafeAvailabilityRule | null = null;
  for (const rule of rules) {
    if (!rule) continue;

    const ruleStart = new Date(rule.startDate);
    const ruleEnd = new Date(rule.endDate);
    ruleStart.setHours(0, 0, 0, 0);
    ruleEnd.setHours(0, 0, 0, 0);

    if (normalized < ruleStart || normalized > ruleEnd) continue;
    if (!rule.daysOfWeek.includes(dayOfWeek)) continue;

    const isExcluded = rule.excludedDates?.some((ed) => {
      const excluded = new Date(ed.date);
      excluded.setHours(0, 0, 0, 0);
      return excluded.getTime() === normalized.getTime();
    });
    if (isExcluded) continue;

    if (
      !bestRule ||
      new Date(rule.createdAt).getTime() > new Date(bestRule.createdAt).getTime()
    ) {
      bestRule = rule;
    }
  }

  if (!bestRule) return null;

  return {
    participants: bestRule.maxParticipants ?? 0,
    extraDivers: bestRule.maxExtraDivers ?? 0,
    extraSnorkelers: bestRule.maxExtraSnorkelers ?? 0,
  };
}

interface UseAvailabilityProps {
  listingId: string;
  availabilityRules: SafeAvailabilityRule[];
  initialMonth: number;
  initialYear: number;
  startTime?: string | null;
  leadHours?: number | null;
}

interface AvailableSpot {
  date: string;
  participants: number;
  extraDivers: number;
  extraSnorkelers: number | null;
}

function findFirstAvailableDate(
  rules: SafeAvailabilityRule[],
  today: Date
): Date | null {
  let earliest: Date | null = null;

  for (const rule of rules) {
    if (!rule) continue;

    const ruleStartDate = new Date(rule.startDate);
    const ruleEndDate = new Date(rule.endDate);
    ruleStartDate.setHours(0, 0, 0, 0);
    ruleEndDate.setHours(0, 0, 0, 0);

    if (ruleEndDate < today) continue;

    const excludedDatesSet = new Set(
      rule.excludedDates?.map((ed) => {
        const d = new Date(ed.date);
        d.setHours(0, 0, 0, 0);
        return d.toDateString();
      }) || []
    );

    let currentDate = new Date(Math.max(ruleStartDate.getTime(), today.getTime()));
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= ruleEndDate) {
      const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay();

      if (
        rule.daysOfWeek.includes(dayOfWeek) &&
        !excludedDatesSet.has(currentDate.toDateString())
      ) {
        if (!earliest || currentDate < earliest) {
          earliest = new Date(currentDate);
        }
        break;
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  return earliest;
}

const useAvailability = ({
  listingId,
  availabilityRules = [],
  initialMonth,
  initialYear,
  startTime,
  leadHours,
}: UseAvailabilityProps) => {
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [availableSpots, setAvailableSpots] = useState<AvailableSpot[]>([]);
  const [disabledDates, setDisabledDates] = useState<Date[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedDateData, setSelectedDateData] = useState<any>(null);
  const [spotsParticipants, setSpotsParticipants] = useState(0);
  const [spotsExtraDivers, setSpotsExtraDivers] = useState(0);
  const [spotsExtraSnorkelers, setSpotsExtraSnorkelers] = useState(0);
  const [isDateValid, setIsDateValid] = useState(true);
  /** Shared by both availability fetchers: stale responses must not write state. */
  const availabilityReqSeq = useRef(0);
  const [currentMonth, setCurrentMonth] = useState(initialMonth);
  const [currentYear, setCurrentYear] = useState(initialYear);
  const [firstAvailableDate, setFirstAvailableDate] = useState<Date | null>(null);
  const [firstAvailableDateFormatted, setFirstAvailableDateFormatted] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);

  const preloadDisabledDates = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const daysInMonth = new Date(currentYear, currentMonth, 0).getDate();
      // First bookable day: activities need 36h notice for the center to review.
      const today = getMinBookableDate(startTime, leadHours);

      const datesToCheck: Date[] = [];
      const disabled: Date[] = [];

      for (let day = 1; day <= daysInMonth; day++) {
        const dateToCheck = new Date(currentYear, currentMonth - 1, day);

        if (dateToCheck >= today) {
          datesToCheck.push(dateToCheck);
        } else {
          disabled.push(dateToCheck);
        }
      }
      
      if (datesToCheck.length > 0) {
        const dateStrings = datesToCheck.map(date => date.toISOString());
        
        const response = await axios.get(
          `/api/listings/${listingId}/availability?isRange=true&dates=${encodeURIComponent(JSON.stringify(dateStrings))}`
        );
        
        const { dateResults } = response.data;
        
        if (dateResults && Array.isArray(dateResults)) {
          dateResults.forEach((result: any, index: number) => {
            if (!result.isAvailable) {
              disabled.push(datesToCheck[index]);
            }
          });
        }
      }
      
      setDisabledDates(disabled);
    } catch (error) {
      if (isRateLimited(error)) {
        console.warn("Availability preload rate limited; calendar will retry on next interaction.");
      } else {
        console.warn("Error preloading disabled dates:", error);
      }
      const minDate = getMinBookableDate(startTime, leadHours);
      const fallbackDisabled: Date[] = [];
      for (let day = 1; day <= new Date(currentYear, currentMonth, 0).getDate(); day++) {
        const d = new Date(currentYear, currentMonth - 1, day);
        if (d < minDate) fallbackDisabled.push(d);
      }
      setDisabledDates(fallbackDisabled);
    } finally {
      setIsLoading(false);
    }
  }, [listingId, currentMonth, currentYear, startTime, leadHours]);

  const generateAvailableDates = useCallback(() => {
    const today = getMinBookableDate(startTime, leadHours);

    const dates: Date[] = [];
    const spotsInfo: AvailableSpot[] = [];

    if (!availabilityRules || availabilityRules.length === 0) {
      setAvailableDates([]);
      setAvailableSpots([]);
      setFirstAvailableDate(null);
      setFirstAvailableDateFormatted("No available dates");
      
      preloadDisabledDates();
      return;
    }

    availabilityRules.forEach((rule) => {
      if (!rule) return;
      
      const ruleStartDate = new Date(rule.startDate);
      const ruleEndDate = new Date(rule.endDate);

      if (ruleEndDate < today) return;

      const excludedDatesSet = new Set(
        rule.excludedDates?.map((ed) => new Date(ed.date).toDateString()) || []
      );

      const currentDate = new Date(Math.max(ruleStartDate.getTime(), today.getTime()));
      const endDate = new Date(Math.min(ruleEndDate.getTime(), new Date(currentYear, currentMonth, 0).getTime()));

      while (currentDate <= endDate) {
        const dayOfWeek = currentDate.getDay() === 0 ? 7 : currentDate.getDay();

        if (rule.daysOfWeek.includes(dayOfWeek) && !excludedDatesSet.has(currentDate.toDateString())) {
        
          dates.push(new Date(currentDate));
          spotsInfo.push({
            date: currentDate.toISOString(),
            participants: rule.maxParticipants,
            extraDivers: rule.maxExtraDivers,
            extraSnorkelers: rule.maxExtraSnorkelers,
          });
        }

        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    setAvailableDates(dates);
    setAvailableSpots(spotsInfo);
 
    preloadDisabledDates();

    const firstDate = findFirstAvailableDate(availabilityRules, today);

    if (firstDate) {
      setFirstAvailableDate(firstDate);
      setFirstAvailableDateFormatted(
        format(firstDate, "EEEE d 'de' MMMM 'de' yyyy", { locale: es })
      );
      setSelectedDate(firstDate);
      fetchAvailabilityForDate(firstDate);
    } else {
      setFirstAvailableDate(null);
      setFirstAvailableDateFormatted("No available dates");
    }
  }, [availabilityRules, currentMonth, currentYear, preloadDisabledDates, startTime, leadHours]);

  useEffect(() => {
    generateAvailableDates();
  }, [generateAvailableDates]);

  const fetchAvailabilityForDate = useCallback(async (date: Date) => {
    // Responses can land out of order (initial today vs the preselected
    // date); only the latest request may write spots/isDateValid, or a
    // stale "today is unbookable" answer leaves Reserve disabled forever.
    const seq = ++availabilityReqSeq.current;
    setIsLoading(true);
    try {
      const response = await axios.get(`/api/listings/${listingId}/availability?date=${date.toISOString()}`);
      if (seq !== availabilityReqSeq.current) return;
      const { availableSpots: spots, isAvailable } = response.data;

      if (!isAvailable) {
        setDisabledDates((prevDisabledDates) => {
          if (!prevDisabledDates.some(d => d.getTime() === date.getTime())) {
            return [...prevDisabledDates, date];
          }
          return prevDisabledDates;
        });
        setSelectedDateData(null);
        setSpotsParticipants(0);
        setSpotsExtraDivers(0);
        setSpotsExtraSnorkelers(0);
        setIsDateValid(false);
        return;
      }

      setSelectedDateData({
        date: date.toISOString(),
        maxParticipants: spots.participants || 0,
        maxExtraDivers: spots.extraDivers || 0,
        maxExtraSnorkelers: spots.extraSnorkelers || 0,
      });

      setSpotsParticipants(spots.participants || 0);
      setSpotsExtraDivers(spots.extraDivers || 0);
      setSpotsExtraSnorkelers(spots.extraSnorkelers || 0);
      setIsDateValid(true);
    } catch (error) {
      if (seq !== availabilityReqSeq.current) return;
      if (isTransientAvailabilityError(error)) {
        const fallback = getClientSpotsForDate(date, availabilityRules);
        if (fallback && fallback.participants > 0) {
          console.warn("Availability API unavailable; using calendar rules.");
          setSelectedDateData({
            date: date.toISOString(),
            maxParticipants: fallback.participants,
            maxExtraDivers: fallback.extraDivers,
            maxExtraSnorkelers: fallback.extraSnorkelers,
          });
          setSpotsParticipants(fallback.participants);
          setSpotsExtraDivers(fallback.extraDivers);
          setSpotsExtraSnorkelers(fallback.extraSnorkelers);
          setIsDateValid(true);
          return;
        }
      }
      console.warn("No available rules for that date");
      setSelectedDateData(null);
      setSpotsParticipants(0);
      setSpotsExtraDivers(0);
      setSpotsExtraSnorkelers(0);
      setIsDateValid(false);

      setDisabledDates((prevDisabledDates) => {
        if (!prevDisabledDates.some(d => d.getTime() === date.getTime())) {
          return [...prevDisabledDates, date];
        }
        return prevDisabledDates;
      });
    } finally {
      setIsLoading(false);
    }
  }, [listingId, availabilityRules]);
  
  const fetchAvailabilityForDateRange = useCallback(async (dates: Date[]) => {
    if (!dates || dates.length === 0) return { 
      availableSpots: { participants: 0, extraDivers: 0, extraSnorkelers: 0 }, 
      isAvailable: false, 
      dateResults: [] 
    };
    
    const seq = ++availabilityReqSeq.current;
    setIsLoading(true);
    try {
      const dateStrings = dates.map(date => date.toISOString());
      const response = await axios.get(
        `/api/listings/${listingId}/availability?isRange=true&dates=${encodeURIComponent(JSON.stringify(dateStrings))}`
      );

      const { availableSpots, isAvailable, dateResults } = response.data;
      const stale = seq !== availabilityReqSeq.current;

      const spots = availableSpots || { participants: 0, extraDivers: 0, extraSnorkelers: 0 };

      if (!stale) {
        setSpotsParticipants(spots.participants || 0);
        setSpotsExtraDivers(spots.extraDivers || 0);
        setSpotsExtraSnorkelers(spots.extraSnorkelers || 0);

        setIsDateValid(isAvailable);
      }

      if (!stale && dateResults && Array.isArray(dateResults)) {
        const newDisabledDates: Date[] = [];
        
        dateResults.forEach((result: any, index: number) => {
          if (!result.isAvailable) {
            newDisabledDates.push(dates[index]);
          }
        });
        
        if (newDisabledDates.length > 0) {
          setDisabledDates(prev => {
            const existingTimestamps = new Set(prev.map(d => d.getTime()));
            const uniqueNewDates = newDisabledDates.filter(d => !existingTimestamps.has(d.getTime()));
            return [...prev, ...uniqueNewDates];
          });
        }
      }

      return { 
        availableSpots: spots, 
        isAvailable: !!isAvailable, 
        dateResults: dateResults || [] 
      };
    } catch (error) {
      const stale = seq !== availabilityReqSeq.current;
      if (isTransientAvailabilityError(error)) {
        const fallback = dates.length > 0 ? getClientSpotsForDate(dates[0], availabilityRules) : null;
        if (fallback && fallback.participants > 0) {
          console.warn("Availability range API unavailable; using calendar rules.");
          if (!stale) {
            setSpotsParticipants(fallback.participants);
            setSpotsExtraDivers(fallback.extraDivers);
            setSpotsExtraSnorkelers(fallback.extraSnorkelers);
            setIsDateValid(true);
          }
          return {
            availableSpots: fallback,
            isAvailable: true,
            dateResults: [],
          };
        }
      }
      console.warn("No available rule for that range");
      if (!stale) {
        setSpotsParticipants(0);
        setSpotsExtraDivers(0);
        setSpotsExtraSnorkelers(0);
        setIsDateValid(false);
      }

      setDisabledDates(prev => {
        const existingTimestamps = new Set(prev.map(d => d.getTime()));
        const uniqueNewDates = dates.filter(d => !existingTimestamps.has(d.getTime()));
        return [...prev, ...uniqueNewDates];
      });
      
      return { 
        availableSpots: { participants: 0, extraDivers: 0, extraSnorkelers: 0 }, 
        isAvailable: false, 
        dateResults: [] 
      };
    } finally {
      setIsLoading(false);
    }
  }, [listingId, availabilityRules]);

  const handleMonthChange = useCallback((month: number, year: number) => {
    setCurrentMonth(month);
    setCurrentYear(year);
    preloadDisabledDates();
  }, [preloadDisabledDates]);

  return {
    availableDates,
    availableSpots,
    disabledDates,
    selectedDate,
    selectedDateData,
    spotsParticipants,
    spotsExtraDivers,
    spotsExtraSnorkelers,
    isDateValid,
    currentMonth,
    currentYear,
    firstAvailableDate,
    firstAvailableDateFormatted,
    isLoading,
    setSelectedDate,
    handleMonthChange,
    fetchAvailabilityForDate,
    fetchAvailabilityForDateRange
  };
};

export default useAvailability;